import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ReactFlow, {
  Background,
  BackgroundVariant,
  MiniMap,
  Controls,
  applyNodeChanges,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
} from "reactflow";
import "reactflow/dist/style.css";
import "@plannel/scss/components/FloChart.scss";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import floChartService from "@plannel/services/mp/flo-chart-service";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

import { buildFloGraph } from "./floGraph";
import { floNodeTypes, floMinimapColor } from "./FloNodes";
import { FLO_KIND } from "./floConstants";
import { getNodeNavActions } from "./floNavActions";

// 노드 종류별로 검색 조건에 쓸 코드 필드(원본 응답 camelCase) — raw 없으면 라벨로 폴백
const NODE_CODE_FIELD = {
  [FLO_KIND.ITEM]: "itemCd",
  [FLO_KIND.RESOURCE]: "resCd",
  [FLO_KIND.ROUTE]: "routeCd",
};

const EMPTY_DATA = { items: [], resources: [], routes: [], boms: [], routings: [], simResources: [], altSupplies: [], altItemsWithBom: [] };

/**
 * 그래프가 새로 만들어질 때(graphKey 변경) 노드 측정이 끝난 뒤 화면에 맞춘다.
 * 커스텀 노드는 DOM 렌더 후에야 크기가 측정되므로, 측정 전 fitView를 호출하면
 * 줌이 잘못 잡혀 일부만 확대돼 보인다(배경 밴드만 보이는 증상). useNodesInitialized로
 * 측정 완료를 기다린 뒤 한 번만 맞춘다. ReactFlow의 자식이라 컨텍스트 훅 사용 가능.
 */
const FitView = ({ graphKey }) => {
  const nodesInitialized = useNodesInitialized();
  const { fitView } = useReactFlow();
  const fittedKey = useRef(null);

  useEffect(() => {
    if (!nodesInitialized || fittedKey.current === graphKey) return;
    fittedKey.current = graphKey;
    fitView({ padding: 0.2, duration: 300, maxZoom: 1 }); // maxZoom 1: 작은/컴팩트 트리도 노드가 자연 크기 이상으로 커지지 않게
  }, [nodesInitialized, graphKey, fitView]);

  return null;
};

/**
 * FLO 차트 캔버스 (reactflow / React Flow v11).
 * - itemCd 변경 시 백엔드에서 BOM 전개 데이터를 조회하여 그래프를 구성
 * - 노드 드래그 이동(nodesDraggable)
 * - 미니맵(이동·줌 가능) / 컨트롤 / 점 배경
 * - 노드 클릭 시 onSelectNode 콜백으로 상세 정보 전달
 * - direction(TB/LR) 변경 시 그래프 재구성 + fitView
 */
const FloChartFlow = ({ itemCd, direction, hideResource, resourceExpanded, hideRatio, hideAltSupply, refreshKey, onToggleResource, onSelectNode, onSearchItem, rootItemCd, setSnackInfo }) => {
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(false);
  // 노드 우클릭 컨텍스트 메뉴: { x, y, kind, code, hasChildren, isRoot } | null
  const [ctxMenu, setCtxMenu] = useState(null);

  // itemCd 변경 시 BOM 전개 데이터 조회 (없으면 빈 그래프)
  useEffect(() => {
    if (!itemCd) {
      setData(EMPTY_DATA);
      return;
    }
    setLoading(true);
    floChartService
      .get(itemCd)
      .then((res) => {
        // 204 No Content 면 res.data 가 빈 문자열이므로 items 유무로 판별
        setData(res?.data?.items ? res.data : EMPTY_DATA);
      })
      .catch((e) => {
        debug(e);
        setData(EMPTY_DATA);
        setSnackInfo?.({ open: true, severity: "error", content: e.message });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCd]);

  // refreshKey가 바뀌면(레이아웃 초기화 버튼) 동일 입력이라도 그래프를 새로 만들어 위치를 재설정한다.
  const graph = useMemo(
    () => buildFloGraph(data, direction, hideResource, resourceExpanded, hideRatio, hideAltSupply),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, direction, hideResource, resourceExpanded, hideRatio, hideAltSupply, refreshKey]
  );
  const nodeTypes = useMemo(() => floNodeTypes, []);

  const [nodes, setNodes] = useNodesState(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges);

  // 결합(◇) 박스별 멤버 노드 id 집합(박스 id 포함). 위치는 드래그마다 바뀌지만 그룹 구성은
  // 그래프 단위로 고정이므로 graph 기준으로만 계산한다(드래그 중 재계산 방지).
  const combMembers = useMemo(() => {
    const m = new Map();
    graph.nodes.forEach((n) => {
      const g = n.data?.combGroup;
      if (!g) return;
      if (!m.has(g)) m.set(g, new Set([g])); // 박스 자신도 함께 이동
      m.get(g).add(n.id);
    });
    return m;
  }, [graph]);

  // 단방향 추종: leader 노드 id -> 따라올 노드 id 집합. 대체 자재 노드가 자기 원자재를 따라간다.
  // combGroup(양방향 그룹)을 재사용할 수 없어 별도 맵으로 둔다 — 원자재는 이미 결합 박스 멤버라
  // combGroup 을 점유하고 있고, 대체 노드를 그 그룹에 넣으면 대체 노드를 끌 때 박스와 형제 구성품
  // 전체가 끌려온다. 추종은 "leader 가 움직이면 나도 움직인다"의 한 방향만 필요하다.
  const followersOf = useMemo(() => {
    const m = new Map();
    graph.nodes.forEach((n) => {
      const leader = n.data?.followsNode;
      if (!leader) return;
      if (!m.has(leader)) m.set(leader, new Set());
      m.get(leader).add(n.id);
    });
    return m;
  }, [graph]);

  // 결합 박스에 속한 구성품을 드래그하면 같은 박스의 형제 구성품 + 박스를 동일한 이동량(델타)만큼 함께 옮긴다.
  // 노드 위치는 저장하지 않으므로(새로고침·방향전환 시 자동 레이아웃으로 초기화) 캔버스 임시 상태만 갱신한다.
  const handleNodesChange = useCallback(
    (changes) => {
      setNodes((prev) => {
        const applied = applyNodeChanges(changes, prev);
        if (combMembers.size === 0 && followersOf.size === 0) return applied;

        const prevById = new Map(prev.map((n) => [n.id, n]));
        // 1) 직접 드래그된 노드의 이동량
        const deltaOf = new Map(); // 노드 id -> {dx, dy}
        const moveByGroup = new Map(); // 결합 그룹 id -> {dx, dy, draggedId}
        const directlyMoved = new Set();
        changes.forEach((ch) => {
          if (ch.type !== "position" || !ch.position) return;
          const before = prevById.get(ch.id);
          if (!before) return;
          directlyMoved.add(ch.id);
          const dx = ch.position.x - before.position.x;
          const dy = ch.position.y - before.position.y;
          if (!dx && !dy) return;
          deltaOf.set(ch.id, { dx, dy });
          const g = before.data?.combGroup;
          if (g) moveByGroup.set(g, { dx, dy, draggedId: ch.id });
        });
        if (deltaOf.size === 0) return applied;

        // 2) 같은 결합 박스의 형제 구성품 + 박스도 같은 델타로 이동
        //    (여기서 delta 를 채워두면 그 구성품의 추종 노드까지 3)에서 함께 따라온다)
        moveByGroup.forEach(({ dx, dy, draggedId }, g) => {
          combMembers.get(g)?.forEach((id) => {
            if (id !== draggedId && !deltaOf.has(id)) deltaOf.set(id, { dx, dy });
          });
        });

        // 3) 추종 노드(대체 자재)는 leader(원자재)의 델타를 그대로 받는다 — 단방향
        followersOf.forEach((followers, leader) => {
          const d = deltaOf.get(leader);
          if (!d) return;
          followers.forEach((id) => {
            if (!deltaOf.has(id)) deltaOf.set(id, d);
          });
        });

        // 4) 직접 드래그분은 applyNodeChanges 가 이미 반영했으므로 제외하고 적용
        return applied.map((n) => {
          const d = deltaOf.get(n.id);
          if (!d || directlyMoved.has(n.id)) return n;
          return { ...n, position: { x: n.position.x + d.dx, y: n.position.y + d.dy } };
        });
      });
    },
    [setNodes, combMembers, followersOf]
  );
  const [rfInstance, setRfInstance] = useState(null);
  const wrapperRef = useRef(null);
  // hover한 노드와 동일 원본(dupKey)인 노드를 함께 강조. 나머지는 흐리게.
  const [hoveredDup, setHoveredDup] = useState(null);

  // 방향/데이터 변경 시 노드/엣지 재구성. 화면 맞춤은 노드 측정 완료 후 <FitView>가 처리.
  useEffect(() => {
    setNodes(graph.nodes);
    setEdges(graph.edges);
    // 데이터 이상/부분 표시 안내(스낵바는 1개만 노출 → 순환을 우선 안내).
    if (graph.cycles?.length) {
      setSnackInfo?.({ open: true, severity: "warning", content: t("floCyclicModel", { detail: graph.cycles.join(", ") }) });
    } else if (graph.truncated) {
      setSnackInfo?.({ open: true, severity: "warning", content: t("floModelTooLarge") });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph]);

  // 그래프가 새로 만들어질 때마다 바뀌는 키(품목·방향·옵션·초기화·노드수). <FitView>가 이 키 단위로 1회 맞춤.
  const graphKey = `${itemCd}|${direction}|${hideResource}|${resourceExpanded}|${hideRatio}|${hideAltSupply}|${refreshKey}|${graph.nodes.length}`;

  // 브라우저 창/컨테이너 크기가 바뀌면(창 리사이즈·분할선 이동·모니터 변경) 그래프를 다시 화면에 맞춘다.
  // React Flow의 fitView는 init·그래프 변경 시에만 동작하므로, 리사이즈는 직접 감지해 재적용한다.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!rfInstance || !el) return;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      // 리사이즈 연속 발생을 한 프레임으로 합쳐 fitView 1회만 호출
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => rfInstance.fitView({ padding: 0.2, maxZoom: 1 }));
    });
    observer.observe(el);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [rfInstance]);

  // dupKey가 있고 실제로 2곳 이상 전개된 노드에 hover할 때만 강조 활성화
  const handleNodeMouseEnter = (_e, node) => {
    if (node.data?.dupKey && node.data?.dupTotal > 1) setHoveredDup(node.data.dupKey);
  };
  const handleNodeMouseLeave = () => setHoveredDup(null);

  // hover 중에는 동일 원본 노드에 flo-peer-hl, 그 외 dup 가능 노드에 flo-peer-dim 클래스를 부여
  const displayNodes = useMemo(() => {
    if (!hoveredDup) return nodes;
    return nodes.map((n) => {
      const k = n.data?.dupKey;
      if (!k) return n;
      return { ...n, className: k === hoveredDup ? "flo-peer-hl" : "flo-peer-dim" };
    });
  }, [nodes, hoveredDup]);

  const handleNodeClick = (_e, node) => {
    // 자원 노드는 펼침/접힘 상태와 무관하게 항상 상세 정보 표시 (펼치기·접기는 상단 툴바 토글로 일원화)
    if (node.data?.kind === FLO_KIND.RESOURCE) {
      onSelectNode({ id: node.id, kind: node.data.kind, role: node.data.role, raw: node.data.raw });
      return;
    }
    // 자원택1 그룹 박스 등 접기 가능한 그룹 클릭 → 접기
    if (node.data?.collapsible) {
      onToggleResource?.(false);
      return;
    }
    // 정션 노드(결합/택일)는 상세 정보가 없으므로 선택 해제 처리
    if (!node.data?.kind) {
      onSelectNode(null);
      return;
    }
    onSelectNode({
      id: node.id,
      kind: node.data.kind,
      role: node.data.role,
      raw: node.data.raw,
      // 대체 자재 노드는 품목 기본 속성 뒤에 대체 공급 정보(대체 대상/투입 공정/우선순위)를 덧붙인다.
      isAltSupply: node.data.isAltSupply,
      altSupplyOf: node.data.altSupplyOf,
      altSupplyRouteCd: node.data.altSupplyRouteCd,
      altSupplyPriority: node.data.altSupplyPriority,
    });
  };

  const handlePaneClick = () => {
    onSelectNode(null);
  };

  // 노드(품목/자원/라우트) 우클릭 → 컨텍스트 메뉴 표시.
  // - 품목: 그 품목코드로 FLO 재조회 + 관련 화면으로 이동
  // - 자원/라우트: 그 코드로 관련 화면으로 이동
  // 정션(결합/택1) 등 kind 없는 노드는 메뉴를 띄우지 않는다.
  const handleNodeContextMenu = (e, node) => {
    const kind = node.data?.kind;
    const codeField = NODE_CODE_FIELD[kind];
    if (!codeField) return;
    const code = node.data?.raw?.[codeField] ?? node.data?.label;
    if (!code) return;
    e.preventDefault(); // 브라우저 기본 메뉴 차단
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      kind,
      code,
      hasChildren: !!node.data?.hasChildren,
      isRoot: !!node.data?.isRoot,
      // 대체 자재 노드: "대체 공급" 화면은 원자재(item_cd) 기준으로 조회해야 그 대체 그룹이 보인다.
      isAltSupply: !!node.data?.isAltSupply,
      altSupplyOf: node.data?.altSupplyOf ?? null,
      hasAltSupply: !!node.data?.hasAltSupply,
    });
  };

  // 품목 노드에서 그 품목코드로 FLO 차트를 재조회(현재 화면 내). 구성품이 있는 품목만 의미 있음.
  const handleFloSearch = () => {
    if (ctxMenu?.code) onSearchItem?.(ctxMenu.code);
    setCtxMenu(null);
  };

  // 검색 필터로 처음 조회했던 원래 루트 품목으로 되돌아간다.
  const handleBackToRoot = () => {
    if (rootItemCd) onSearchItem?.(rootItemCd);
    setCtxMenu(null);
  };

  // 노드 코드를 검색 조건으로 심고(updateViewState) 대상 탭을 연다(updateHistoryState → App.js 워처가 처리).
  const handleNavigate = (action) => {
    const { reduxKey } = action;
    // payload 는 코드 + 우클릭 컨텍스트(위치·생산품목 등)를 함께 받아 화면별 검색조건을 만든다.
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(reduxKey, action.payload(ctxMenu.code, ctxMenu))));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    setCtxMenu(null);
  };

  // 현재 우클릭한 노드에서 이동 가능한 액션 목록.
  //   테넌트/사용자 권한 밖 화면은 아예 빼 둔다 — 사이드바에 없는 화면이 우클릭 메뉴에만 남으면 안 된다.
  const navActions = ctxMenu ? getNodeNavActions(ctxMenu).filter((a) => isPermitted(a.reduxKey)) : [];
  // 품목 노드이면서 구성품이 있을 때만 FLO 재조회 액션 노출
  const canFloSearch = ctxMenu?.kind === FLO_KIND.ITEM && ctxMenu?.hasChildren;

  // 품목 노드가 하나도 없으면(미선택 또는 데이터 없음) 안내 오버레이 표시
  const isEmpty = !graph.nodes.some((n) => n.id.startsWith("item-"));

  return (
    <Box ref={wrapperRef} sx={{ width: "100%", height: "100%" }}>
      <ReactFlow
        className="flo-chart"
        nodes={displayNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onNodeContextMenu={handleNodeContextMenu}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onPaneClick={handlePaneClick}
        onInit={setRfInstance}
        nodesDraggable
        minZoom={0.05}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        proOptions={{ hideAttribution: true }}
      >
        <FitView graphKey={graphKey} />
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="#dfe3e8" />
        <MiniMap nodeColor={floMinimapColor} nodeStrokeColor={floMinimapColor} nodeStrokeWidth={3} pannable zoomable />
        <Controls />

        {loading && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && isEmpty && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5, pointerEvents: "none" }}>
            <Typography variant="body2" color="text.secondary">
              {t("floSelectItemHint")}
            </Typography>
          </Box>
        )}
      </ReactFlow>

      {/* 노드 우클릭 메뉴 — FLO 재조회(품목) + 관련 화면으로 이동.
          권한 필터로 이동 액션이 전부 빠지고 재조회도 불가능하면 노드 코드만 남은 빈 메뉴가 되므로 아예 열지 않는다. */}
      <Menu
        open={!!ctxMenu && (canFloSearch || navActions.length > 0)}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        // 기본값(false)이면 메뉴가 열릴 때 body 스크롤을 잠그며 스크롤바 너비만큼 padding-right를
        // 추가해 컨테이너 너비가 바뀐다. 그 미세 변화를 wrapperRef의 ResizeObserver가 감지해
        // fitView()를 다시 불러 차트 줌이 재조정되므로, 레이아웃을 건드리지 않도록 잠금을 끈다.
        disableScrollLock
        PaperProps={{ sx: { borderRadius: 1.5, minWidth: 210, boxShadow: 4, mt: 0.5 } }}
        MenuListProps={{ dense: true, sx: { py: 0.5 } }}
      >
        {/* 우클릭한 노드 코드 표시(헤더) */}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2, pt: 0.5, pb: 0.75 }}>
          {ctxMenu?.code}
        </Typography>

        {canFloSearch && (
          <MenuItem onClick={handleFloSearch} sx={{ gap: 1.25, py: 0.75, px: 1.5, mx: 0.5, borderRadius: 1 }}>
            <SearchIcon fontSize="small" sx={{ color: "primary.main" }} />
            <Typography variant="body2">{t("floSearchByItem", { item: ctxMenu?.code ?? "" })}</Typography>
          </MenuItem>
        )}
        {canFloSearch && rootItemCd && rootItemCd !== itemCd && (
          <MenuItem onClick={handleBackToRoot} sx={{ gap: 1.25, py: 0.75, px: 1.5, mx: 0.5, borderRadius: 1 }}>
            <RestartAltIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="body2">{t("floBackToSearch", { item: rootItemCd })}</Typography>
          </MenuItem>
        )}

        {canFloSearch && navActions.length > 0 && <Divider sx={{ my: 0.5 }} />}

        {navActions.map((action) => (
          <MenuItem key={action.id} onClick={() => handleNavigate(action)} sx={{ gap: 1.25, py: 0.75, px: 1.5, mx: 0.5, borderRadius: 1 }}>
            <OpenInNewIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography variant="body2">{t(action.titleKey)}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default FloChartFlow;
